import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Mail, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEmailSyncsStore } from "@/store/emailSyncsStore";
import { useConnectionsStore } from "@/store/connectionsStore";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { SettingsRow } from "@/modules/standardAgent/components/SettingsRow";
import { PIPEDREAM_PROVIDER_ID } from "@forgepoint/shared";

const MICROSOFT_OUTLOOK_APP_ID = "microsoft_outlook";

/**
 * Self-contained email integrations settings panel.
 * Follows the standard settings UX pattern with section labels, cards, and skeleton loading.
 * @returns Email integrations settings panel element
 */
export function EmailIntegrationsSettings() {
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [isCreatingSync, setIsCreatingSync] = useState(false);
  const [syncToDelete, setSyncToDelete] = useState<string | null>(null);

  const {
    fetchEmailSyncs,
    createEmailSync,
    deleteEmailSync,
    getAllEmailSyncs,
    emailSyncStates,
    globalLoadingState,
  } = useEmailSyncsStore();

  const { fetchConnectionsByApp, getConnectionsByApp } = useConnectionsStore();

  const emailSyncs = getAllEmailSyncs();
  const outlookConnections = getConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);

  const emailSyncsWithConnections = useMemo(() => {
    return emailSyncs.map((sync) => {
      const connection = outlookConnections.find(
        (c) => c.id === sync.connectionId,
      );
      return { ...sync, connection };
    });
  }, [emailSyncs, outlookConnections]);

  const syncedConnectionIds = useMemo(() => {
    return emailSyncs.map((sync) => sync.connectionId);
  }, [emailSyncs]);

  useEffect(() => {
    fetchEmailSyncs();
    fetchConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);
  }, [fetchEmailSyncs, fetchConnectionsByApp]);

  /**
   * Handles selecting a connection and creating an email sync for it.
   * @param connectionId - The ID of the connection to sync
   */
  const handleConnectionSelect = useCallback(
    async (connectionId: string) => {
      const existingSync = emailSyncs.find(
        (sync) => sync.connectionId === connectionId,
      );

      if (existingSync) {
        toast.info("This connection already has an email sync");
        setSelectedConnectionId(null);
        return;
      }

      try {
        setIsCreatingSync(true);
        const emailSync = await createEmailSync(connectionId);

        if (emailSync) {
          toast.success("Email sync created successfully");
          await fetchEmailSyncs();
          await fetchConnectionsByApp(MICROSOFT_OUTLOOK_APP_ID);
        } else {
          toast.error("Failed to create email sync");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create email sync";
        toast.error(errorMessage);
      } finally {
        setIsCreatingSync(false);
        setSelectedConnectionId(null);
      }
    },
    [emailSyncs, createEmailSync, fetchEmailSyncs, fetchConnectionsByApp],
  );

  /**
   * Confirms deletion of the currently-targeted email sync.
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!syncToDelete) {
      return;
    }

    const success = await deleteEmailSync(syncToDelete);
    if (success) {
      toast.success("Email sync deleted");
    } else {
      toast.error("Failed to delete email sync");
    }
    setSyncToDelete(null);
  }, [syncToDelete, deleteEmailSync]);

  const isLoading = globalLoadingState.isLoadingFetchAll;

  if (isLoading) {
    return <EmailIntegrationsSkeleton />;
  }

  if (globalLoadingState.fetchAllError) {
    return (
      <div className="space-y-6 pt-4">
        <Card className="border-destructive/40 shadow-sm">
          <CardContent className="py-6 text-sm text-destructive">
            {globalLoadingState.fetchAllError}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 pt-4">
        {/* ── Connect ── */}
        <SectionLabel title="Connect" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="email-integrations-outlook"
              label="Microsoft Outlook"
              description="Connect an Outlook mailbox to sync emails for agent access."
            >
              <div className="w-[260px]">
                <ConnectionSelector
                  appId={MICROSOFT_OUTLOOK_APP_ID}
                  providerId={PIPEDREAM_PROVIDER_ID}
                  connectionId={selectedConnectionId}
                  onSelect={handleConnectionSelect}
                  disabledConnectionIds={syncedConnectionIds}
                  disabled={isCreatingSync}
                />
              </div>
            </SettingsRow>
          </div>
          {isCreatingSync && (
            <div className="flex items-center gap-2 px-4 pb-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Creating email sync...</span>
            </div>
          )}
        </Card>

        {/* ── Active Syncs ── */}
        <SectionLabel title="Active Syncs" />
        {emailSyncsWithConnections.length === 0 ? (
          <Card className="shadow-sm overflow-hidden">
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <Mail className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">
                No email syncs configured yet.
              </p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Connect an account above to start syncing emails.
              </p>
            </div>
          </Card>
        ) : (
          <Card className="shadow-sm overflow-hidden">
            <div className="divide-y">
              {emailSyncsWithConnections.map((sync) => (
                <EmailSyncRow
                  key={sync.id}
                  syncId={sync.id}
                  connectionName={sync.connection?.name ?? null}
                  lastSyncedAt={sync.lastSyncedAt}
                  createdAt={sync.createdAt}
                  isDeleting={
                    emailSyncStates.get(sync.id)?.isLoadingDelete ?? false
                  }
                  onDelete={() => setSyncToDelete(sync.id)}
                />
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* ── Delete Confirmation Dialog ── */}
      <DeleteSyncDialog
        open={!!syncToDelete}
        onOpenChange={() => setSyncToDelete(null)}
        isDeleting={
          emailSyncStates.get(syncToDelete ?? "")?.isLoadingDelete ?? false
        }
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

// ── Local Components ──

interface EmailSyncRowProps {
  /** Unique identifier for the email sync */
  syncId: string;
  /** Display name of the connection, or null if unavailable */
  connectionName: string | null;
  /** Timestamp of last successful sync */
  lastSyncedAt: Date | null;
  /** Timestamp when the sync was created */
  createdAt: Date;
  /** Whether a delete operation is in progress */
  isDeleting: boolean;
  /** Callback to initiate deletion */
  onDelete: () => void;
}

/**
 * A single email sync displayed as a settings row with connection info and delete action.
 * @param props - EmailSyncRowProps
 * @returns Email sync row element
 */
function EmailSyncRow({
  connectionName,
  lastSyncedAt,
  createdAt,
  isDeleting,
  onDelete,
}: EmailSyncRowProps) {
  return (
    <SettingsRow
      label={connectionName ?? "Microsoft Outlook"}
      description={`Created ${formatDate(createdAt)}`}
      expandedContent={
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>Last synced: {formatDate(lastSyncedAt)}</span>
        </div>
      }
    >
      <div className="flex items-center gap-2">
        <Badge
          variant="secondary"
          className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
        >
          Connected
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive transition-colors"
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </Button>
      </div>
    </SettingsRow>
  );
}

interface DeleteSyncDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the open state changes */
  onOpenChange: () => void;
  /** Whether the delete operation is in progress */
  isDeleting: boolean;
  /** Callback to confirm deletion */
  onConfirm: () => void;
}

/**
 * Confirmation dialog for deleting an email sync.
 * @param props - DeleteSyncDialogProps
 * @returns Delete confirmation dialog element
 */
function DeleteSyncDialog({
  open,
  onOpenChange,
  isDeleting,
  onConfirm,
}: DeleteSyncDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Email Sync</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this email sync? This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onOpenChange}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            Delete
            {isDeleting && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Skeleton loading state matching the EmailIntegrationsSettings layout.
 * Shows placeholder rows for the Connect and Active Syncs sections.
 * @returns Skeleton layout element
 */
function EmailIntegrationsSkeleton() {
  return (
    <div className="space-y-6 pt-4">
      <Skeleton className="h-5 w-16 ml-2" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          <SkeletonRow />
        </div>
      </Card>

      <Skeleton className="h-5 w-24 ml-2" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 2 }).map((_, i) => (
            <SkeletonRow key={`sync-${i}`} />
          ))}
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SettingsRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-8 w-[200px] rounded-md" />
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}

/**
 * Formats a date for display, returning "Never" for null values.
 * @param date - The date to format, or null
 * @returns Formatted date string
 */
function formatDate(date: Date | null): string {
  if (!date) {
    return "Never";
  }
  return new Date(date).toLocaleString();
}
